import { Button, dialog, InputFactory } from "nq-component";
import BaseListPresenter from "../../../base/BaseListPresenter";
import { upsertUseCase } from "../../../usecases/object";
import Queue from "nq";
import {
  PASSWORD_TITLE,
  passwordInputProps,
  isStrongPassword,
} from "../../../components/InputFactory/passwordPolicy";

class UsersPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }

  init() {
    this.limit = 50;
    // this.where = { account: { $exists: false } };
    this.where = { account: { $ne: "jobPortal" }, roles: {$exists: true} };
    this.search = {};
    this.filter = {};
    this.include = ["employee"];
    this.keys = []; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  confirmChangePass = async (data, pass) => {
    if (this._resettingPass) return;
    if (!isStrongPassword(pass)) {
      this.view.showError(PASSWORD_TITLE);
      return;
    }
    this._resettingPass = true;
    try {
      dialog.close();
      this.view.showProgressDialog();
      data.password = pass;
      data.isReset = false;
      await upsertUseCase().execute("users", data);
      this.view.hideProgressDialog();
      this.view.showSuccess(`${data.username} Successfully Change Password`);
      this.view.refetchData();
    } catch (e) {
      this.view.hideProgressDialog();
      this.view.showError(e?.message || e);
      console.error("User page", e);
    } finally {
      this._resettingPass = false;
    }
  };

  // reset password
  handleResetPass(data) {
    let newPass = "";
    dialog.fire({
      html: (
        <>
          <div className="p-2">
            <h4>Reset Password Confirmation</h4>
            <hr />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                this.confirmChangePass(data, newPass);
              }}
            >
              <p style={{ fontSize: "20px" }}>
                <strong>User: {data.username}</strong> ?
              </p>
              <label>
                New Password<span className="text-danger">*</span>
              </label>
              <InputFactory
                type="Password"
                required
                {...passwordInputProps()}
                onChange={(value) => {
                  newPass = value;
                }}
              />
              <hr />
              <div className="mt-4 mb-4 d-flex justify-content-end">
                <Button
                  type="button"
                  className="btn btn-light fs-sm me-3"
                  onClick={() => dialog.close()}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn btn-primary fs-sm me-3">
                  Confirm
                </Button>
              </div>
            </form>
          </div>
        </>
      ),
      footer: false,
    });
  }

  removeFingerPrint(object) {
    // const data = {
    //   id: object.id,
    //   employee: { id: object.employee.id },
    //   fingerPrint: [],
    //   fingerFid: [],
    //   fingerSize: [],
    // };
    const biometricID = object?.employee?.biometricId || object.employee.id;

    dialog.fire({
      html: (
        <>
          <div className="p-2">
            <h4>Remove Fingerprint Confirmation</h4>
            <hr />
            <p style={{ fontSize: "20px" }}>
              <strong>User: {object.username}</strong> ?
            </p>
            <hr />
            <div className="mt-4 mb-4 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-light fs-sm me-3"
                onClick={() => dialog.close()}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    dialog.close();
                    this.view.showProgressDialog();
                    const biometricFinger =
                      await this.findObjectUseCase.execute("biometric_prints", {
                        where: {
                          $or: [
                            { "data.PIN": String(biometricID) },
                            { "data.PIN": biometricID },
                          ],
                        },
                        keys: ["id"],
                      });
                    if (biometricFinger.length > 0) {
                      for (const finger of biometricFinger) {
                        await new Queue.Document().delete(
                          "biometric_prints",
                          finger.id,
                        );
                      }
                    }
                    dialog.close();
                    this.view.showSuccess(`Successfully Remove Fingerprint`);
                  } catch (e) {
                    dialog.close();
                    this.view.showError(e.message || e);
                    console.error(e.message);
                  } finally {
                    this.view.refetchData();
                  }
                }}
                className="btn btn-primary fs-sm me-3"
              >
                confirm
              </button>
            </div>
          </div>
        </>
      ),
      footer: false,
    });
  }
}

export default UsersPresenter;
